// Forklaring av statusene: hva hver status betyr, hvilken e-post som følger
// med, og hva produsenten ser på leveranselenken.
//
// Rekkefølgen og tekstene følger Utvikling | BMAx s. 7-8. E-postene er de
// aktive automasjonsreglene; hva produsenten ser følger RESPONDABLE_STATUSES i
// backend/src/services/vendorLink.js. Endres en regel der, må teksten her følge
// etter — dette er en forklaring, ikke en avlesning av oppsettet.
import PropTypes from "prop-types";
import { Dialog, DialogContent, DialogTitle, Grid } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { statusKey, statusLabel } from "utils/statusLabels";

const INK = "#344767";
const MUTED = "#67748e";
const LINE = "#c8d3e0";
const BLUE = "#006bb7";
const GREEN = "#0f8a43";
const RED = "#c0392b";

// Hva produsenten møter på /leveranse/<token> i hver status.
const LINK_FORM = "Bekreft / avslå";
const LINK_ANSWER = "Viser svaret produsenten ga";
const LINK_CLOSED = "«Ordren venter ikke på svar fra dere nå»";
const LINK_REQUEST = "Egen forespørselslenke (fastpris / ta over)";

// Eksportert: statusinnstillingene viser `text` og `link` for én status.
export const STATUSES = [
  {
    key: "quoting",
    text: "Ordren er opprettet. Pris og informasjon kontrolleres. Settes automatisk når ordren registreres.",
    email: "Kunden får e-post om at ordren er mottatt.",
    link: LINK_CLOSED,
  },
  {
    key: "pending",
    text: "Prisen er satt, og ordren venter på betaling fra kunden.",
    email:
      "Kunden får e-post med betalingslenke. Påminnelse sendes 3 døgn etter at ordren ble opprettet. Etter 30 døgn uten betaling kanselleres ordren automatisk.",
    link: LINK_CLOSED,
  },
  {
    key: "processing",
    text: "Kunden har betalt, og leveringstid avklares. Settes automatisk når betalingen kommer inn.",
    email: "Kunden får e-post om at betalingen er mottatt.",
    link: LINK_FORM,
  },
  {
    key: "awaiting-vendor",
    text: "Den offisielle bestillingen er sendt til produsenten, som skal bekrefte eller avslå. Et tidligere svar fra produsenten nullstilles.",
    email: "Produsenten får bestillingen med leveranselenken.",
    link: LINK_FORM,
  },
  {
    key: "vendor-confirmed",
    text: "Produsenten har bekreftet ordren og leveringstidspunktet. Settes automatisk når produsenten bekrefter på lenken.",
    email: "Admin får e-post om bekreftelsen.",
    link: LINK_ANSWER,
  },
  {
    key: "vendor-rejected",
    text: "Produsenten har avslått. Settes automatisk når produsenten avslår på lenken. Velg ny produsent med «Endre leverandør», og sett ordren til «Venter på produsent» igjen.",
    email: "Admin får e-post om avslaget.",
    link: LINK_ANSWER,
  },
  {
    key: "scheduled",
    text: "Ordren er bekreftet, og levering er planlagt.",
    email: "Kunden får leveringsbekreftelse. Påminnelse sendes 24 timer før levering.",
    link: LINK_ANSWER,
  },
  {
    key: "completed",
    text: "Ordren er levert og ferdigstilt.",
    email: "Kunden får e-post om at ordren er fullført.",
    link: LINK_ANSWER,
  },
  {
    key: "awaiting-quote",
    text: "Forespørselsløpet: forespørselen er sendt til én eller flere produsenter, og vi venter på fastpris. Velg produsent og pris, og sett deretter ordren til «Klar til betaling».",
    email: "Produsentene får forespørselen med hver sin lenke.",
    link: LINK_REQUEST,
  },
  {
    key: "sold-closed",
    text: "En produsent har tatt over forespørselen og følger opp kunden direkte. Overtakelsen er registrert for fakturering. Statusen kan ikke endres etterpå.",
    email: "Produsenten, kunden og de andre produsentene får beskjed.",
    link: LINK_REQUEST,
  },
  {
    key: "cancelled",
    text: "Ordren er avsluttet uten levering. Settes manuelt, eller automatisk etter 30 døgn uten betaling.",
    email: "Ingen e-post.",
    link: LINK_CLOSED,
  },
  {
    key: "refunded",
    text: "Beløpet er betalt tilbake til kunden.",
    email: "Kunden får e-post når refusjonen registreres.",
    link: LINK_CLOSED,
  },
];

// ------------------------------- diagrammet -------------------------------
const W = 120;
const H = 48;
const STEP = 152;
const MAIN_Y = 116;
const TOP_Y = 14;
const BOTTOM_Y = 214;

// `lines` er etiketten brutt for hånd: SVG-tekst brytes ikke selv.
const NODES = [
  { key: "quoting", x: 10, y: MAIN_Y, lines: ["Kvalitetssikres"] },
  { key: "pending", x: 10 + STEP, y: MAIN_Y, lines: ["Klar til", "betaling"] },
  { key: "processing", x: 10 + STEP * 2, y: MAIN_Y, lines: ["Behandler"] },
  { key: "awaiting-vendor", x: 10 + STEP * 3, y: MAIN_Y, lines: ["Venter på", "produsent"] },
  { key: "vendor-confirmed", x: 10 + STEP * 4, y: MAIN_Y, lines: ["Bekreftet av", "produsent"] },
  { key: "scheduled", x: 10 + STEP * 5, y: MAIN_Y, lines: ["Klar til", "levering"] },
  { key: "completed", x: 10 + STEP * 6, y: MAIN_Y, lines: ["Fullført"], tone: "good" },
  { key: "awaiting-quote", x: 86, y: TOP_Y, lines: ["Venter på", "tilbud"] },
  { key: "sold-closed", x: 390, y: TOP_Y, lines: ["Solgt og", "avsluttet"], tone: "good" },
  { key: "cancelled", x: 10 + STEP, y: BOTTOM_Y, lines: ["Kansellert"], tone: "bad" },
  { key: "vendor-rejected", x: 10 + STEP * 3, y: BOTTOM_Y, lines: ["Avvist av", "produsent"], tone: "bad" },
];

const MID = MAIN_Y + H / 2;
const EDGES = [
  // hovedløpet
  ...[0, 1, 2, 3, 4, 5].map((i) => ({ from: [10 + STEP * i + W, MID], to: [10 + STEP * (i + 1), MID] })),
  // forespørselsløpet
  { from: [70, MAIN_Y], to: [120, TOP_Y + H], label: "forespørsel", lx: 84, ly: 92, anchor: "end" },
  { from: [172, TOP_Y + H], to: [222, MAIN_Y], label: "fastpris valgt", lx: 208, ly: 86, anchor: "start" },
  { from: [206, TOP_Y + H / 2], to: [390, TOP_Y + H / 2], label: "produsent tar over", lx: 298, ly: 30, anchor: "middle" },
  // ubetalt
  { from: [222, MAIN_Y + H], to: [222, BOTTOM_Y], label: "30 døgn uten betaling", lx: 230, ly: 193, anchor: "start", dashed: true },
  // avslag og ny runde
  { from: [506, MAIN_Y + H], to: [506, BOTTOM_Y], label: "avslår", lx: 500, ly: 193, anchor: "end" },
  { from: [546, BOTTOM_Y], to: [546, MAIN_Y + H], label: "ny produsent", lx: 552, ly: 193, anchor: "start" },
];

const TONE = {
  good: { stroke: GREEN, fill: "#e7f6ed", text: "#0b3d22" },
  bad: { stroke: RED, fill: "#fdecea", text: "#7a1f16" },
  plain: { stroke: BLUE, fill: "#eaf2fb", text: "#0b3a5c" },
};

function FlowDiagram({ current }) {
  return (
    // Under 900 px ville etikettene blitt uleselige; da ruller diagrammet sidelengs.
    <SoftBox sx={{ overflowX: "auto", border: `1px solid ${LINE}`, borderRadius: "10px", p: 1, background: "#fff" }}>
      <svg
        viewBox="0 0 1052 272"
        role="img"
        aria-label="Diagram over statusløpet for en ordre"
        style={{ display: "block", width: "100%", minWidth: 900, height: "auto" }}
      >
        <defs>
          <marker id="status-info-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0 0 L10 5 L0 10 z" fill={MUTED} />
          </marker>
        </defs>

        {EDGES.map((e) => (
          <g key={`${e.from}-${e.to}`}>
            <line
              x1={e.from[0]}
              y1={e.from[1]}
              x2={e.to[0]}
              y2={e.to[1]}
              stroke={MUTED}
              strokeWidth="1.5"
              strokeDasharray={e.dashed ? "5 4" : undefined}
              markerEnd="url(#status-info-arrow)"
            />
            {e.label && (
              <text x={e.lx} y={e.ly} textAnchor={e.anchor} fontSize="11" fill={MUTED}>
                {e.label}
              </text>
            )}
          </g>
        ))}

        {NODES.map((n) => {
          const isCurrent = n.key === current;
          const tone = TONE[n.tone || "plain"];
          const firstLine = n.y + H / 2 - (n.lines.length - 1) * 7 + 4;
          return (
            <g key={n.key}>
              <rect
                x={n.x}
                y={n.y}
                width={W}
                height={H}
                rx="9"
                fill={isCurrent ? INK : tone.fill}
                stroke={isCurrent ? INK : tone.stroke}
                strokeWidth={isCurrent ? 3 : 1.5}
              />
              {n.lines.map((line, i) => (
                <text
                  key={line}
                  x={n.x + W / 2}
                  y={firstLine + i * 14}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="700"
                  fill={isCurrent ? "#fff" : tone.text}
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </SoftBox>
  );
}

FlowDiagram.propTypes = { current: PropTypes.string };
FlowDiagram.defaultProps = { current: "" };

const RULES = [
  "«Endre status» viser e-posten som hører til statusen, og du kan rette den før du sender. Du velger selv: «Send e-post og endre status», «Endre status uten e-post» eller «Avbryt», som lukker uten å endre noe.",
  "E-postene sendes ikke umiddelbart. Regn med opptil et par minutter.",
  "Produsenten kan bare bekrefte eller avslå når ordren står i «Behandler» eller «Venter på produsent». I alle andre statuser viser leveranselenken «Ordren venter ikke på svar fra dere nå», eller svaret produsenten allerede har gitt.",
  "Kunden ser «Venter på produsent» også når ordren står i «Bekreftet av produsent» eller «Avvist av produsent». De to er interne steg.",
  "Alle statusendringer og e-poster loggføres i hendelsesloggen på ordren.",
];

function StatusInfoDialog({ open, currentStatus, onClose }) {
  const current = statusKey(currentStatus);
  // En ordre i en eldre status (f.eks. «På vent») finnes ikke i diagrammet.
  const inDiagram = NODES.some((n) => n.key === current);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>Slik fungerer statusene</DialogTitle>
      <DialogContent>
        {current && (
          <SoftTypography variant="button" fontWeight="regular" color="text" display="block" mb={1.5} sx={{ textTransform: "none" }}>
            Denne ordren står i <strong>«{statusLabel(current)}»</strong>
            {inDiagram ? ", markert med mørk boks i diagrammet." : ", en eldre status som ikke er med i løpet under."}
          </SoftTypography>
        )}

        <FlowDiagram current={current} />
        <SoftTypography variant="caption" color="text" display="block" mt={1}>
          Midtrekken er en vanlig ordre. Øverst er forespørselsløpet, der produsenter gir fastpris eller
          tar over kunden. Nederst er utfallene som stopper eller sender ordren en runde tilbake.
        </SoftTypography>

        <SoftTypography variant="button" fontWeight="bold" display="block" mt={3} mb={1}>
          Statusene
        </SoftTypography>
        <Grid container spacing={1.5}>
          {STATUSES.map((s) => {
            const isCurrent = s.key === current;
            return (
              <Grid item xs={12} md={6} key={s.key}>
                <SoftBox
                  sx={{
                    height: "100%",
                    border: `${isCurrent ? 2 : 1}px solid ${isCurrent ? INK : LINE}`,
                    borderRadius: "10px",
                    p: 1.5,
                  }}
                >
                  <SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: INK, textTransform: "none" }}>
                    {statusLabel(s.key)}
                    {isCurrent ? " (nåværende)" : ""}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5} sx={{ fontSize: 13 }}>
                    {s.text}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.75} sx={{ fontSize: 13 }}>
                    <strong>E-post:</strong> {s.email}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.25} sx={{ fontSize: 13 }}>
                    <strong>Produsentens lenke:</strong> {s.link}
                  </SoftTypography>
                </SoftBox>
              </Grid>
            );
          })}
        </Grid>

        <SoftTypography variant="button" fontWeight="bold" display="block" mt={3} mb={1}>
          Regler som gjelder alle statuser
        </SoftTypography>
        <SoftBox component="ul" sx={{ m: 0, pl: 2.5 }}>
          {RULES.map((rule) => (
            <SoftTypography
              component="li"
              variant="caption"
              color="text"
              key={rule}
              sx={{ fontSize: 13, display: "list-item", mb: 0.5 }}
            >
              {rule}
            </SoftTypography>
          ))}
        </SoftBox>

        <SoftTypography variant="caption" color="text" display="block" mt={2} sx={{ fontSize: 13 }}>
          Eldre statuser som «På vent», «Avventer betaling», «Betaling feilet» og «Levert» kan stå på gamle
          ordre eller komme fra WooCommerce. «På vent» settes ofte automatisk når kunden starter betalingen
          uten å fullføre med en gang. «Mislyktes» og «Betaling feilet» sender betalingspåminnelse til kunden.
        </SoftTypography>

        <SoftBox mt={3} display="flex" justifyContent="flex-end">
          <SoftButton size="small" variant="gradient" color="info" onClick={onClose}>
            Lukk
          </SoftButton>
        </SoftBox>
      </DialogContent>
    </Dialog>
  );
}

StatusInfoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  currentStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

StatusInfoDialog.defaultProps = { currentStatus: "" };

export default StatusInfoDialog;
